/**
 * MonthlyHistoryView — Sous-vue "Historique mensuel & rapport mois par mois"
 *
 * Liste verticale de cartes pliables (un mois par carte, le plus récent en haut).
 * Pour chaque mois affiche : CA total, ventilation par catégorie/département,
 * achats/dépenses (avec mode de paiement), locations.
 * Bouton "PDF" qui imprime un rapport mensuel.
 *
 * Source : GET /api/reports/monthly-history?start_year=2026&start_month=1
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar, TrendingUp, ChevronDown, ChevronRight, FileText,
  Wine, Utensils, Gamepad2, Receipt, Wallet, CreditCard, Smartphone,
  RefreshCw, Loader2, Printer, Banknote,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n || 0)));

const MONTH_NAMES = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const PAYMENT_META = {
  cash:   { label: "Espèces",   Icon: Banknote,   cls: "bg-emerald-500/15 text-emerald-300" },
  mobile: { label: "Mobile",    Icon: Smartphone, cls: "bg-blue-500/15 text-blue-300" },
  cheque: { label: "Chèque",    Icon: CreditCard, cls: "bg-amber-500/15 text-amber-300" },
  wallet: { label: "Bon client", Icon: Wallet,    cls: "bg-fuchsia-500/15 text-fuchsia-300" },
};

const GROUP_META = {
  bar:         { label: "Bar (boissons)", Icon: Wine,     cls: "text-cyan-300" },
  menu_combos: { label: "Plats & menus",  Icon: Utensils, cls: "text-orange-300" },
  jeux:        { label: "Jeux",           Icon: Gamepad2, cls: "text-purple-300" },
  autres:      { label: "Autres",         Icon: Receipt,  cls: "text-slate-300" },
};

/** Génère et ouvre un PDF pour un mois donné. */
function openMonthlyPdf(m) {
  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) {
    toast.error("Bloqueur de popups actif — autorisez les fenêtres pour imprimer");
    return;
  }
  const monthLabel = `${MONTH_NAMES[m.month - 1]} ${m.year}`;
  const totalIncome = (m.total_revenue || 0) + (m.locations_advances || 0);
  const marge = totalIncome - (m.expenses_total || 0);

  const groupRows = Object.entries(GROUP_META).map(([k, meta]) => `
    <tr><td>${meta.label}</td><td style="text-align:right;font-family:monospace">${fmt(m.by_revenue_group?.[k] || 0)} F</td></tr>
  `).join("");

  const paymentRows = Object.entries(PAYMENT_META).map(([k, meta]) => `
    <tr><td>${meta.label}</td><td style="text-align:right;font-family:monospace">${fmt(m.by_payment?.[k] || 0)} F</td></tr>
  `).join("");

  const expCatRows = Object.entries(m.by_expense_category || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `<tr><td>${k}</td><td style="text-align:right;font-family:monospace">${fmt(v)} F</td></tr>`)
    .join("");

  const expPayRows = Object.entries(m.by_expense_payment_mode || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => {
      const lbl = k === "fonds_propres" ? "Fonds propres"
        : k === "caisse_restau" ? "Caisse Restau"
        : k === "mobile" ? "Mobile Money"
        : k === "cash" ? "Espèces"
        : k === "cheque" ? "Chèque"
        : k === "wallet" ? "Bon client"
        : "Autre";
      return `<tr><td>${lbl}</td><td style="text-align:right;font-family:monospace">${fmt(v)} F</td></tr>`;
    }).join("");

  const html = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="utf-8">
<title>Rapport mensuel — ${monthLabel}</title>
<style>
  body{font-family:Arial,sans-serif;margin:24px;color:#222}
  h1{margin:0 0 4px 0;font-size:22px;color:#1a3a52}
  h2{font-size:14px;color:#1a3a52;margin:14px 0 6px 0;border-bottom:1px solid #ddd;padding-bottom:3px}
  .meta{font-size:11px;color:#666;margin-bottom:10px}
  .kpis{display:flex;gap:8px;flex-wrap:wrap;margin:10px 0}
  .kpi{background:#f4f6fa;border:1px solid #d3d8e0;border-radius:6px;padding:8px 12px;font-size:11px;min-width:140px}
  .kpi b{display:block;font-size:16px;color:#1a3a52;margin-top:2px}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-top:4px}
  th,td{border:1px solid #ccd0d8;padding:5px 8px;text-align:left}
  th{background:#1a3a52;color:#fff;font-weight:bold}
  .footer{margin-top:18px;font-size:9px;color:#777;border-top:1px solid #ddd;padding-top:6px}
  .two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  @media print { body{margin:10px} }
</style></head>
<body>
  <h1>Rapport mensuel — ${monthLabel}</h1>
  <div class="meta">Espace Maxo · Généré le ${new Date().toLocaleString("fr-FR")}</div>
  <div class="kpis">
    <div class="kpi"><span>CA Caisse</span><b>${fmt(m.total_revenue)} F</b></div>
    <div class="kpi" style="background:#f5ecfc"><span>Total Locations</span><b style="color:#6b21a8">${fmt(m.locations_total)} F</b></div>
    <div class="kpi" style="background:#e2f5e9"><span>Avances Locations</span><b style="color:#197d3a">${fmt(m.locations_advances)} F</b></div>
    <div class="kpi" style="background:#fff3e0"><span>Solde locations à encaisser</span><b style="color:#b07a00">${fmt(m.locations_balance_due)} F</b></div>
    <div class="kpi" style="background:#e2f5e9"><span>TOTAL Recettes (CA + avances)</span><b style="color:#197d3a">${fmt(totalIncome)} F</b></div>
    <div class="kpi" style="background:#fdecec"><span>Dépenses & Achats</span><b style="color:#a01010">${fmt(m.expenses_total)} F</b></div>
    <div class="kpi" style="background:${marge >= 0 ? "#e7f5ec" : "#fde2e2"}"><span>Marge brute</span><b style="color:${marge >= 0 ? "#197d3a" : "#a01010"}">${fmt(marge)} F</b></div>
    <div class="kpi"><span>Factures validées</span><b>${m.invoice_count}</b></div>
  </div>
  <div class="two-col">
    <div>
      <h2>CA Caisse par catégorie</h2>
      <table>${groupRows}</table>
    </div>
    <div>
      <h2>CA Caisse par mode de paiement</h2>
      <table>${paymentRows}</table>
    </div>
  </div>
  <h2>Locations & Réservations (${m.locations_count}) — Total ${fmt(m.locations_total)} F · Avances ${fmt(m.locations_advances)} F · Solde ${fmt(m.locations_balance_due)} F</h2>
  ${(m.locations_details || []).length === 0
    ? '<p style="font-size:11px;color:#666">Aucune location ce mois.</p>'
    : `<table><tr><th>Date</th><th>Client</th><th style="text-align:right">Total</th><th style="text-align:right">Avance</th><th style="text-align:right">Solde</th><th>Statut</th></tr>${
        (m.locations_details || []).map(l => `<tr><td>${l.reservation_date || ""}</td><td>${l.client_name || "—"}</td><td style="text-align:right;font-family:monospace">${fmt(l.rental_amount)} F</td><td style="text-align:right;font-family:monospace;color:#197d3a">${fmt(l.deposit_paid)} F</td><td style="text-align:right;font-family:monospace;color:${(l.balance_remaining || 0) > 0 ? "#b07a00" : "#999"}">${fmt(l.balance_remaining || 0)} F</td><td>${l.status || ""}</td></tr>`).join("")
      }</table>`}
  <div class="two-col">
    <div>
      <h2>Dépenses par catégorie</h2>
      ${expCatRows ? `<table><tr><th>Catégorie</th><th style="text-align:right">Montant</th></tr>${expCatRows}</table>` : '<p style="font-size:11px;color:#666">Aucune dépense ce mois.</p>'}
    </div>
    <div>
      <h2>Dépenses par mode de paiement</h2>
      ${expPayRows ? `<table><tr><th>Mode</th><th style="text-align:right">Montant</th></tr>${expPayRows}</table>` : '<p style="font-size:11px;color:#666">Aucune dépense ce mois.</p>'}
    </div>
  </div>
  <div class="footer">
    Rapport généré par Caisse Pro — Espace Maxo. Toutes les valeurs sont en F CFA.<br>
    <b>Total Locations</b> = montant total facturé sur le mois. <b>Avances</b> = effectivement encaissé. <b>Solde</b> = reste dû par les clients.<br>
    Les factures transférées (assigned_week) sont prises en compte sur leur mois cible.
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},300)}</script>
</body></html>`;
  w.document.write(html);
  w.document.close();
}

const MonthCard = ({ m, open, onToggle }) => {
  const totalIncome = (m.total_revenue || 0) + (m.locations_advances || 0);
  const marge = totalIncome - (m.expenses_total || 0);
  const monthLabel = `${MONTH_NAMES[m.month - 1]} ${m.year}`;
  return (
    <div
      className={`mhc-card rounded-xl border transition-colors ${open ? "bg-slate-800/70 border-amber-500/40" : "bg-slate-900/50 border-slate-800 hover:border-slate-700"}`}
      data-testid={`monthly-card-${m.month_label}`}
    >
      {/* Header épuré : chevron + mois + encaissé + marge + PDF */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3.5 py-3 text-left"
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-amber-300 flex-shrink-0" strokeWidth={2.4} />
          : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={2.4} />}
        <div className="min-w-0 flex-1">
          <p className="text-white font-black text-[14px] tracking-tight leading-tight truncate">{monthLabel}</p>
          <p className="text-[11px] text-slate-500 leading-tight">
            <span className="font-mono font-semibold text-emerald-300">{fmt(totalIncome)}</span>
            <span className="text-emerald-300/60"> F encaissé</span>
            <span className="text-slate-600 mx-1">·</span>
            <span className={`font-mono font-semibold ${marge >= 0 ? "text-amber-300" : "text-rose-300"}`}>{fmt(marge)}</span>
            <span className={marge >= 0 ? "text-amber-300/60" : "text-rose-300/60"}> F marge</span>
            {m.invoice_count > 0 && (
              <>
                <span className="text-slate-600 mx-1">·</span>
                <span className="font-mono">{m.invoice_count}</span>
                <span> fact.</span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openMonthlyPdf(m); }}
          className="flex items-center gap-1 h-8 px-2.5 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-[11.5px] font-bold border border-cyan-500/30 flex-shrink-0"
          data-testid={`monthly-pdf-${m.month_label}`}
          title="Imprimer / télécharger PDF"
        >
          <Printer className="w-3.5 h-3.5" strokeWidth={2.4} />
          <span className="hidden sm:inline">PDF</span>
        </button>
      </button>

      {open && (
        <div className="px-3.5 pb-3.5 pt-1 space-y-3 text-xs border-t border-slate-800/60">
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* BILAN MENSUEL — tableau lisible type compte de résultat     */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="mhc-bilan mt-2 rounded-xl overflow-hidden border border-slate-700/60 bg-slate-950/50">
            <style>{`
              .mhc-bilan-row { display: flex; align-items: center; padding: 7px 12px; gap: 8px; }
              .mhc-bilan-row.indent { padding-left: 24px; }
              .mhc-bilan-row.indent-2 { padding-left: 34px; font-size: 11.5px; }
              .mhc-bilan-lbl { flex: 1; min-width: 0; color: rgb(203, 213, 225); font-size: 12.5px; }
              .mhc-bilan-val { font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 12.5px; text-align: right; white-space: nowrap; }
              .mhc-bilan-section {
                background: rgba(51, 65, 85, 0.5);
                border-bottom: 1px solid rgba(71, 85, 105, 0.4);
                border-top: 1px solid rgba(71, 85, 105, 0.4);
              }
              .mhc-bilan-section .mhc-bilan-lbl {
                font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 700;
              }
              .mhc-bilan-total {
                background: rgba(30, 41, 59, 0.7);
                border-top: 1px solid rgba(71, 85, 105, 0.5);
              }
              .mhc-bilan-total .mhc-bilan-lbl { font-weight: 700; color: white; }
              .mhc-bilan-total .mhc-bilan-val { font-size: 13.5px; }
              .mhc-bilan-marge {
                background: linear-gradient(90deg, rgba(15, 118, 110, 0.15) 0%, rgba(6, 182, 212, 0.12) 100%);
                border-top: 2px solid rgba(20, 184, 166, 0.4);
                padding: 12px 12px;
              }
              .mhc-bilan-marge.neg {
                background: linear-gradient(90deg, rgba(190, 18, 60, 0.15) 0%, rgba(244, 63, 94, 0.12) 100%);
                border-top-color: rgba(244, 63, 94, 0.5);
              }
              .mhc-bilan-marge .mhc-bilan-lbl { font-size: 12px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: white; }
              .mhc-bilan-marge .mhc-bilan-val { font-size: 18px; }
              .mhc-bilan-empty { color: rgb(100, 116, 139); font-style: italic; font-size: 11.5px; }
            `}</style>

            {/* Titre du tableau */}
            <div className="mhc-bilan-row" style={{ background: 'rgba(15, 23, 42, 0.6)', borderBottom: '1px solid rgba(71, 85, 105, 0.4)' }}>
              <FileText className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" strokeWidth={2.4} />
              <span className="mhc-bilan-lbl" style={{ color: 'white', fontWeight: 700, fontSize: '12px', letterSpacing: '0.05em' }}>
                Bilan mensuel — {monthLabel}
              </span>
            </div>

            {/* ── RECETTES ── */}
            <div className="mhc-bilan-row mhc-bilan-section">
              <span className="mhc-bilan-lbl text-emerald-300">Recettes</span>
              <span className="mhc-bilan-val text-emerald-300">+</span>
            </div>
            <div className="mhc-bilan-row indent">
              <span className="mhc-bilan-lbl">CA Caisse</span>
              <span className="mhc-bilan-val text-amber-300">{fmt(m.total_revenue)} F</span>
            </div>
            {(m.locations_advances || 0) > 0 && (
              <div className="mhc-bilan-row indent">
                <span className="mhc-bilan-lbl">Avances Locations reçues</span>
                <span className="mhc-bilan-val text-emerald-300">{fmt(m.locations_advances)} F</span>
              </div>
            )}
            <div className="mhc-bilan-row mhc-bilan-total">
              <span className="mhc-bilan-lbl">Total Recettes</span>
              <span className="mhc-bilan-val text-emerald-300">{fmt(totalIncome)} F</span>
            </div>

            {/* ── DÉPENSES (toujours affichées) ── */}
            <div className="mhc-bilan-row mhc-bilan-section">
              <span className="mhc-bilan-lbl text-rose-300">Dépenses</span>
              <span className="mhc-bilan-val text-rose-300">−</span>
            </div>
            {(m.expenses_total || 0) === 0 && (
              <div className="mhc-bilan-row indent">
                <span className="mhc-bilan-empty">Aucune dépense sur ce mois.</span>
              </div>
            )}
            {(m.expenses_total || 0) > 0 && Object.entries(m.by_expense_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, val]) => (
              <div key={cat} className="mhc-bilan-row indent">
                <span className="mhc-bilan-lbl truncate">{cat}</span>
                <span className="mhc-bilan-val text-rose-300">{fmt(val)} F</span>
              </div>
            ))}
            {(m.expenses_total || 0) > 0 && Object.entries(m.by_expense_payment_mode || {}).filter(([, v]) => v > 0).length > 0 && (
              <>
                <div className="mhc-bilan-row indent" style={{ paddingTop: 2, paddingBottom: 2 }}>
                  <span className="mhc-bilan-lbl text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Par mode de paiement</span>
                </div>
                {Object.entries(m.by_expense_payment_mode || {}).filter(([, v]) => v > 0).map(([k, v]) => {
                  const lbl = k === "fonds_propres" ? "Fonds propres"
                    : k === "caisse_restau" ? "Caisse Restau"
                    : k === "mobile" ? "Mobile Money"
                    : k === "cash" ? "Espèces"
                    : k === "cheque" ? "Chèque"
                    : k === "wallet" ? "Bon client"
                    : "Autre";
                  return (
                    <div key={k} className="mhc-bilan-row indent-2">
                      <span className="mhc-bilan-lbl text-slate-400">{lbl}</span>
                      <span className="mhc-bilan-val text-rose-300/80">{fmt(v)} F</span>
                    </div>
                  );
                })}
              </>
            )}
            <div className="mhc-bilan-row mhc-bilan-total">
              <span className="mhc-bilan-lbl">Total Dépenses</span>
              <span className="mhc-bilan-val text-rose-300">{fmt(m.expenses_total || 0)} F</span>
            </div>

            {/* ── MARGE NETTE ── */}
            <div className={`mhc-bilan-row mhc-bilan-marge ${marge < 0 ? 'neg' : ''}`}>
              <TrendingUp className={`w-4 h-4 flex-shrink-0 ${marge >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} strokeWidth={2.4} />
              <span className="mhc-bilan-lbl">Marge nette</span>
              <span className={`mhc-bilan-val ${marge >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                {marge >= 0 ? '+' : ''}{fmt(marge)} F
              </span>
            </div>
          </div>

          {/* Détail Locations (Avances/Solde) — visible si locations */}
          {(m.locations_total || 0) > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg p-2 bg-emerald-950/40 border border-emerald-900/50">
                <p className="text-[9.5px] uppercase tracking-wider text-emerald-300/80 font-bold">Avances reçues</p>
                <p className="text-[13px] font-black text-emerald-200 font-mono">{fmt(m.locations_advances)}<span className="text-[10px] text-emerald-400/70 ml-0.5">F</span></p>
              </div>
              <div className="rounded-lg p-2 bg-amber-950/40 border border-amber-900/50">
                <p className="text-[9.5px] uppercase tracking-wider text-amber-300/80 font-bold">Solde à encaisser</p>
                <p className="text-[13px] font-black text-amber-200 font-mono">{fmt(m.locations_balance_due)}<span className="text-[10px] text-amber-400/70 ml-0.5">F</span></p>
              </div>
            </div>
          )}

          {/* CA par catégorie — sober */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" strokeWidth={2.4} /> CA par catégorie
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(GROUP_META).map(([k, meta]) => {
                const Ic = meta.Icon;
                const val = m.by_revenue_group?.[k] || 0;
                return (
                  <div key={k} className="flex items-center gap-2 p-1.5 rounded-md bg-slate-900/40 border border-slate-800/60">
                    <Ic className={`w-3.5 h-3.5 flex-shrink-0 ${meta.cls.split(' ')[1] || 'text-slate-300'}`} strokeWidth={2.2} />
                    <span className="text-[11px] text-slate-400 flex-1 truncate">{meta.label}</span>
                    <span className="text-[12px] font-mono font-bold text-white">{fmt(val)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CA par mode de paiement */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Mode de paiement (CA)</p>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(PAYMENT_META).map(([k, meta]) => {
                const Ic = meta.Icon;
                const val = m.by_payment?.[k] || 0;
                return (
                  <div key={k} className="flex items-center gap-2 p-1.5 rounded-md bg-slate-900/40 border border-slate-800/60">
                    <Ic className={`w-3.5 h-3.5 flex-shrink-0 ${meta.cls.split(' ')[1] || 'text-slate-300'}`} strokeWidth={2.2} />
                    <span className="text-[11px] text-slate-400 flex-1 truncate">{meta.label}</span>
                    <span className="text-[12px] font-mono font-bold text-white">{fmt(val)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Locations détails (table) */}
          {(m.locations_details || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                Locations & Réservations · <span className="font-mono normal-case">{m.locations_count}</span>
              </p>
              <div className="bg-slate-900/40 rounded-lg border border-slate-800/60 max-h-44 overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead className="sticky top-0 bg-slate-900">
                    <tr className="text-slate-500">
                      <th className="text-left py-1.5 px-2 font-semibold">Date</th>
                      <th className="text-left py-1.5 px-2 font-semibold">Client</th>
                      <th className="text-right py-1.5 px-2 font-semibold">Total</th>
                      <th className="text-right py-1.5 px-2 font-semibold">Avance</th>
                      <th className="text-right py-1.5 px-2 font-semibold">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(m.locations_details || []).map((l) => (
                      <tr key={l.id} className="border-t border-slate-800/40">
                        <td className="py-1 px-2 text-slate-400 font-mono">{l.reservation_date}</td>
                        <td className="py-1 px-2 text-slate-200 truncate max-w-[100px]">{l.client_name || "—"}</td>
                        <td className="py-1 px-2 text-right font-mono text-purple-300">{fmt(l.rental_amount)}</td>
                        <td className="py-1 px-2 text-right font-mono text-emerald-300">{fmt(l.deposit_paid)}</td>
                        <td className={`py-1 px-2 text-right font-mono ${(l.balance_remaining || 0) > 0 ? "text-amber-300" : "text-slate-600"}`}>
                          {fmt(l.balance_remaining || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-700 bg-slate-800/60">
                      <td className="py-1.5 px-2 text-[10px] uppercase text-slate-400 font-bold" colSpan={2}>Total</td>
                      <td className="py-1.5 px-2 text-right font-mono font-bold text-purple-200">{fmt(m.locations_total)}</td>
                      <td className="py-1.5 px-2 text-right font-mono font-bold text-emerald-300">{fmt(m.locations_advances)}</td>
                      <td className="py-1.5 px-2 text-right font-mono font-bold text-amber-300">{fmt(m.locations_balance_due)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Dépenses — intégrées dans le Bilan mensuel plus haut */}
        </div>
      )}
    </div>
  );
};

const MonthlyHistoryView = () => {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIds, setOpenIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/monthly-history`, {
        params: { start_year: 2026, start_month: 1 },
        timeout: 60000,
      });
      const list = r.data.months || [];
      setMonths(list);
      // Ouvrir automatiquement le mois en cours
      if (list.length > 0) {
        setOpenIds(new Set([list[0].month_label]));
      }
    } catch (e) {
      toast.error("Erreur chargement historique mensuel");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totals = useMemo(() => {
    return months.reduce((acc, m) => {
      acc.revenue += m.total_revenue || 0;
      acc.locations_total += m.locations_total || 0;
      acc.locations_advances += m.locations_advances || 0;
      acc.locations_balance_due += m.locations_balance_due || 0;
      acc.expenses += m.expenses_total || 0;
      acc.invoices += m.invoice_count || 0;
      return acc;
    }, { revenue: 0, locations_total: 0, locations_advances: 0, locations_balance_due: 0, expenses: 0, invoices: 0 });
  }, [months]);

  return (
    <div className="mhv-root space-y-3" data-testid="monthly-history-view">
      <style>{`
        .mhv-root, .mhv-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
        .mhv-kpi {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.4);
          border-left-width: 3px;
          flex: 1 1 140px; min-width: 140px;
        }
        .mhv-kpi-label { font-size: 9.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: rgb(148, 163, 184); }
        .mhv-kpi-value { font-size: 15px; font-weight: 800; font-family: 'JetBrains Mono', monospace; }
      `}</style>

      {/* Header compact — 1 ligne titre + actualiser */}
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0" strokeWidth={2.2} />
        <div className="flex-1 min-w-0">
          <p className="text-white font-black text-[15px] tracking-tight leading-tight truncate">Historique mensuel</p>
          <p className="text-slate-500 text-[11px] leading-tight">Depuis Janvier 2026</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="w-9 h-9 rounded-lg bg-slate-800/70 hover:bg-slate-800 border border-slate-700/60 flex items-center justify-center text-slate-300 hover:text-white flex-shrink-0"
          data-testid="monthly-refresh"
          title="Actualiser"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={2.4} />}
        </button>
      </div>

      {/* Totaux cumulés — 5 KPI en strip flex-wrap */}
      <div className="flex flex-wrap gap-2">
        <div className="mhv-kpi" style={{ borderLeftColor: '#f59e0b' }}>
          <TrendingUp className="w-4 h-4 text-amber-400 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="mhv-kpi-label">CA Cumulé</p>
            <p className="mhv-kpi-value text-amber-300">{fmt(totals.revenue)}<span className="text-[10px] text-amber-400/70 ml-0.5">F</span></p>
          </div>
        </div>
        <div className="mhv-kpi" style={{ borderLeftColor: '#a855f7' }}>
          <Calendar className="w-4 h-4 text-purple-400 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="mhv-kpi-label">Locations</p>
            <p className="mhv-kpi-value text-purple-300">{fmt(totals.locations_total)}<span className="text-[10px] text-purple-400/70 ml-0.5">F</span></p>
          </div>
        </div>
        <div className="mhv-kpi" style={{ borderLeftColor: '#10b981' }}>
          <Banknote className="w-4 h-4 text-emerald-400 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="mhv-kpi-label">Avances</p>
            <p className="mhv-kpi-value text-emerald-300">{fmt(totals.locations_advances)}<span className="text-[10px] text-emerald-400/70 ml-0.5">F</span></p>
          </div>
        </div>
        <div className="mhv-kpi" style={{ borderLeftColor: '#f59e0b' }}>
          <Wallet className="w-4 h-4 text-amber-400 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="mhv-kpi-label">Solde restant</p>
            <p className="mhv-kpi-value text-amber-300">{fmt(totals.locations_balance_due)}<span className="text-[10px] text-amber-400/70 ml-0.5">F</span></p>
          </div>
        </div>
        <div className="mhv-kpi" style={{ borderLeftColor: '#f43f5e' }}>
          <Receipt className="w-4 h-4 text-rose-400 flex-shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="mhv-kpi-label">Dépenses</p>
            <p className="mhv-kpi-value text-rose-300">{fmt(totals.expenses)}<span className="text-[10px] text-rose-400/70 ml-0.5">F</span></p>
          </div>
        </div>
      </div>

      {loading && months.length === 0 && (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
        </div>
      )}

      {!loading && months.length === 0 && (
        <div className="py-12 text-center text-slate-500 rounded-xl bg-slate-900/40 border border-dashed border-slate-800/60">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-[13px]">Aucun mois disponible depuis Janvier 2026.</p>
        </div>
      )}

      <div className="space-y-2">
        {months.map((m) => (
          <MonthCard
            key={m.month_label}
            m={m}
            open={openIds.has(m.month_label)}
            onToggle={() => toggle(m.month_label)}
          />
        ))}
      </div>
    </div>
  );
};

export default MonthlyHistoryView;
